export const hours = Array.from({ length: 15 }, (_, index) => index + 7)

export type ScenarioId = 'base' | 'device' | 'deviceHybrid' | 'hybrid' | 'combined' | 'hybridProcess'

export type ScenarioFlags = {
  device: boolean
  hybrid: boolean
  process: boolean
}

export type SimulationInputs = {
  annualCases: number
  inclusionShare: number
  deviceUsage: number
  baseRate: number
  shortStayWeight: number
  fullStayWeight: number
  hybridRevenue: number
  mdAuditRate: number
  reducedCaseShare: number
  reducedCaseValue: number
  physicianMultiplier: number
  physicianMinutes: number
  fullAuditRate: number
  fullReducedCaseShare: number
  fullReducedCaseValue: number
  deviceCount: number
  deviceCost: number
  opportunityCostPerDay: number
  marginalContributionRate: number
  growthCap: number
  reliefShare: number
  bindingFactor: number
  dischargeWithout: number
  dischargeWith: number
  ambulationWithout: number
  ambulationWith: number
  bleedingWithout: number
  bleedingWith: number
  qalyLoss: number
  relevantBleedRate: number
  bedsByHour: number[]
  inflowByHour: number[]
}

export type ScenarioDefinition = {
  id: ScenarioId
  name: string
  shortName: string
  flags: ScenarioFlags
  description: string
  tone: 'neutral' | 'teal' | 'blue' | 'slate'
}

export type OccupancySimulation = {
  data: Array<{ hour: string; occupied: number; capacity: number; utilisation: number | null; aboveCapacity: boolean }>
  peakOccupied: number
  peakUtilisation: number
  capacityWarnings: number
  averageUtilisation: number
}

export type EconomicBreakdown = {
  relevantCases: number
  shortStayGross: number
  shortStayAfterAudit: number
  fullStayRevenue: number
  hybridRevenue: number
  hybridSwitchBenefit: number
  marginNewHybrid: number
  marginNewStandard: number
  deviceOnlyAdditional: number
  processOnlyAdditional: number
  combinedAdditional: number
  bleedReduction: number
  effectiveRelief: number
}

export type ScenarioResult = {
  id: ScenarioId
  name: string
  shortName: string
  flags: ScenarioFlags
  tone: ScenarioDefinition['tone']
  additionalCases: number
  additionalCasesPct: number
  contribution: number
  contributionPerCase: number
  personnelRelief: number
  qaly: number
  timeSavedMinutes: number
  deviceCost: number
  occupancy: OccupancySimulation
  formula: string
  rationale: string
}

export const scenarioDefinitions: ScenarioDefinition[] = [
  {
    id: 'base',
    name: 'Ausgangslage',
    shortName: 'Basis',
    flags: { device: false, hybrid: false, process: false },
    description: 'Vergleichsmaßstab ohne Umstellung von Technik, Abrechnung oder AWR-Prozess.',
    tone: 'neutral',
  },
  {
    id: 'device',
    name: 'Verschlusssystem allein',
    shortName: 'System',
    flags: { device: true, hybrid: false, process: false },
    description: 'Kürzere Entlassfähigkeit bei unverändertem Abrechnungs- und AWR-Prozess.',
    tone: 'teal',
  },
  {
    id: 'deviceHybrid',
    name: 'Verschlusssystem + Hybrid-DRG',
    shortName: 'System + Hybrid',
    flags: { device: true, hybrid: true, process: false },
    description: 'Kombination aus dem klinischen Effekt und dem Wechsel der Abrechnungsart.',
    tone: 'blue',
  },
  {
    id: 'hybrid',
    name: 'Hybrid-DRG ohne Verschlusssystem',
    shortName: 'Hybrid',
    flags: { device: false, hybrid: true, process: false },
    description: 'Reiner Abrechnungseffekt ohne klinische oder prozessuale Änderung.',
    tone: 'slate',
  },
  {
    id: 'combined',
    name: 'Verschlusssystem + Hybrid-DRG + Prozessanpassung',
    shortName: 'Kombiniert',
    flags: { device: true, hybrid: true, process: true },
    description: 'Vollständiges Zielbild mit früher AWR-Entlassung nach Mobilisierungsfähigkeit.',
    tone: 'teal',
  },
  {
    id: 'hybridProcess',
    name: 'Hybrid-DRG + Prozessanpassung ohne Verschlusssystem',
    shortName: 'Hybrid + Prozess',
    flags: { device: false, hybrid: true, process: true },
    description: 'Abrechnung und Prozessumstellung ohne reduziertes Blutungsrisiko.',
    tone: 'slate',
  },
]

export const defaultInputs: SimulationInputs = {
  annualCases: 1,
  inclusionShare: 1,
  deviceUsage: 0.5,
  baseRate: 4561.37,
  shortStayWeight: 1.546,
  fullStayWeight: 1.961,
  hybridRevenue: 7436,
  mdAuditRate: 0.1,
  reducedCaseShare: 0.5,
  reducedCaseValue: 5500,
  physicianMultiplier: 4,
  physicianMinutes: 60,
  fullAuditRate: 0.1,
  fullReducedCaseShare: 0.5,
  fullReducedCaseValue: 7800,
  deviceCount: 1,
  deviceCost: 120,
  opportunityCostPerDay: 564.8,
  marginalContributionRate: 0.1752,
  growthCap: 0.25,
  reliefShare: 0.5,
  bindingFactor: 0.7,
  dischargeWithout: 340,
  dischargeWith: 270,
  ambulationWithout: 269,
  ambulationWith: 109,
  bleedingWithout: 0.242,
  bleedingWith: 0.095,
  qalyLoss: 0.23,
  relevantBleedRate: 0.023,
  bedsByHour: [20, 20, 20, 20, 20, 20, 20, 20, 20, 20, 20, 20, 20, 0, 0],
  inflowByHour: [0, 1, 1, 2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
}

const nonNegative = (value: number) => Math.max(0, Number.isFinite(value) ? value : 0)
const safeDivide = (numerator: number, denominator: number) => denominator > 0 ? numerator / denominator : 0

export function simulateOccupancy(inflow: number[], capacity: number[], durationMinutes: number): OccupancySimulation {
  const data = hours.map((hour, index) => {
    const occupied = inflow.reduce((sum, count, sourceIndex) => {
      const stillInRecovery = index >= sourceIndex && durationMinutes - ((index - sourceIndex) * 60) > 0
      return sum + (stillInRecovery ? nonNegative(count) : 0)
    }, 0)
    const beds = nonNegative(capacity[index] ?? 0)
    const utilisation = beds > 0 ? occupied / beds : null
    return {
      hour: `${hour} Uhr`,
      occupied,
      capacity: beds,
      utilisation,
      aboveCapacity: occupied > beds && beds > 0,
    }
  })

  const activeHours = data.filter(point => point.utilisation !== null)
  const peakOccupied = Math.max(0, ...data.map(point => point.occupied))
  const peakUtilisation = Math.max(0, ...activeHours.map(point => point.utilisation ?? 0))
  const capacityWarnings = data.filter(point => point.aboveCapacity || (point.capacity === 0 && point.occupied > 0)).length
  const averageUtilisation = activeHours.length > 0
    ? activeHours.reduce((sum, point) => sum + (point.utilisation ?? 0), 0) / activeHours.length
    : 0

  return { data, peakOccupied, peakUtilisation, capacityWarnings, averageUtilisation }
}

export function calculateEconomics(input: SimulationInputs): EconomicBreakdown {
  const relevantCases = nonNegative(input.annualCases * input.inclusionShare)
  const auditRisk = nonNegative(input.mdAuditRate * input.reducedCaseShare)
  const fullAuditRisk = nonNegative(input.fullAuditRate * input.fullReducedCaseShare)
  const shortStayGross = input.shortStayWeight * input.baseRate * (1 - auditRisk)
    + input.reducedCaseValue * auditRisk + 222.38
  const shortStayAfterAudit = shortStayGross
    - input.physicianMinutes * input.physicianMultiplier * input.mdAuditRate
  const fullStayRevenue = input.fullStayWeight * input.baseRate * (1 - fullAuditRisk)
    + input.fullReducedCaseValue * fullAuditRisk + 667.14
  const hybridSwitchBenefit = input.hybridRevenue - shortStayAfterAudit + input.opportunityCostPerDay
  const marginNewHybrid = hybridSwitchBenefit + (input.hybridRevenue - hybridSwitchBenefit) * input.marginalContributionRate
  const marginNewStandard = (shortStayAfterAudit - hybridSwitchBenefit) * input.marginalContributionRate
  const deviceCases = relevantCases * input.deviceUsage
  const timeSavedCombined = Math.max(0, input.dischargeWithout - input.ambulationWith) * deviceCases
  const potentialCombined = safeDivide(timeSavedCombined, input.ambulationWith)
  const investedInNewCases = Math.max(0, 1 - input.reliefShare)
  const growthLimit = relevantCases * input.growthCap
  // Excel-Logik hiddencalc!C12: Das Wachstumslimit greift, solange das theoretische Potenzial darunter liegt.
  const combinedAdditional = potentialCombined * investedInNewCases < (1 + input.growthCap) * relevantCases
    ? growthLimit
    : potentialCombined * investedInNewCases
  const deviceOnlyAdditional = safeDivide(
    Math.max(0, input.dischargeWithout - input.dischargeWith) * deviceCases,
    input.dischargeWith,
  ) * investedInNewCases
  const processOnlyAdditional = safeDivide(
    relevantCases * Math.max(0, input.dischargeWithout - input.ambulationWithout),
    input.ambulationWithout,
  ) * investedInNewCases
  const bleedReduction = safeDivide(input.bleedingWithout - input.bleedingWith, input.bleedingWithout)
  const effectiveRelief = input.reliefShare * input.bindingFactor

  return {
    relevantCases,
    shortStayGross,
    shortStayAfterAudit,
    fullStayRevenue,
    hybridRevenue: input.hybridRevenue,
    hybridSwitchBenefit,
    marginNewHybrid,
    marginNewStandard,
    deviceOnlyAdditional,
    processOnlyAdditional,
    combinedAdditional,
    bleedReduction,
    effectiveRelief,
  }
}

function occupancyForScenario(input: SimulationInputs, flags: ScenarioFlags) {
  const duration = flags.device
    ? (flags.process ? input.ambulationWith : input.dischargeWith)
    : (flags.process ? input.ambulationWithout : input.dischargeWithout)
  return simulateOccupancy(input.inflowByHour, input.bedsByHour, duration)
}

export function calculateScenario(input: SimulationInputs, definition: ScenarioDefinition): ScenarioResult {
  const economics = calculateEconomics(input)
  const { flags } = definition
  let additionalCases = 0
  let contribution = 0
  let personnelRelief = 0
  let timeSavedMinutes = 0
  let formula = 'Keine Umstellung: Referenzwert 0.'
  let rationale = 'Die Ausgangslage dient ausschließlich als Vergleichsmaßstab.'

  const deviceCostFor = (additional: number) => (
    (economics.relevantCases + additional) * input.deviceUsage * input.deviceCount * input.deviceCost
  )
  const qalyFor = (additional: number) => (
    (economics.relevantCases + additional) * input.relevantBleedRate * input.qalyLoss * economics.bleedReduction
  )

  if (flags.device && !flags.hybrid && !flags.process) {
    additionalCases = economics.deviceOnlyAdditional
    contribution = economics.marginNewStandard * additionalCases - deviceCostFor(additionalCases)
    personnelRelief = (1 - safeDivide(input.dischargeWith, input.dischargeWithout)) * economics.effectiveRelief
    timeSavedMinutes = Math.max(0, input.dischargeWithout - input.dischargeWith) * economics.relevantCases * input.deviceUsage
    formula = 'DB = Grenzkosten-DB Standard × zusätzliche Fälle − Systemkosten.'
    rationale = 'Die Entlassfähigkeit verkürzt sich, die Abrechnungsart bleibt unverändert.'
  } else if (flags.device && flags.hybrid && !flags.process) {
    additionalCases = economics.deviceOnlyAdditional
    contribution = economics.hybridSwitchBenefit * economics.relevantCases
      + economics.marginNewHybrid * additionalCases
      - deviceCostFor(additionalCases)
    personnelRelief = (1 - safeDivide(input.dischargeWith, input.dischargeWithout)) * economics.effectiveRelief
    timeSavedMinutes = Math.max(0, input.dischargeWithout - input.dischargeWith) * economics.relevantCases * input.deviceUsage
    formula = 'DB = Wechselvorteil Bestandsfälle + Grenzkosten-DB neue Fälle − Systemkosten.'
    rationale = 'Der Abrechnungseffekt wird mit verkürzter Entlassfähigkeit kombiniert.'
  } else if (!flags.device && flags.hybrid && !flags.process) {
    contribution = economics.hybridSwitchBenefit * economics.relevantCases
    formula = 'DB = Wechselvorteil je Bestandsfall × relevante Fälle.'
    rationale = 'Der Effekt entsteht ausschließlich aus dem Wechsel der Abrechnungsart.'
  } else if (flags.device && flags.hybrid && flags.process) {
    additionalCases = economics.combinedAdditional
    contribution = economics.hybridSwitchBenefit * economics.relevantCases
      + economics.marginNewHybrid * additionalCases
      - deviceCostFor(additionalCases)
    personnelRelief = (1 - safeDivide(input.ambulationWith, input.dischargeWithout)) * economics.effectiveRelief
    timeSavedMinutes = Math.max(0, input.dischargeWithout - input.ambulationWith) * economics.relevantCases * input.deviceUsage
    formula = 'DB = Wechselvorteil + Grenzkosten-DB aus kapazitätsbegrenztem Wachstum − Systemkosten.'
    rationale = 'Die AWR-Entlassung orientiert sich an der Mobilisierungsfähigkeit; das Wachstum wird durch das Limit begrenzt.'
  } else if (!flags.device && flags.hybrid && flags.process) {
    additionalCases = economics.processOnlyAdditional
    contribution = economics.hybridSwitchBenefit * economics.relevantCases
      + economics.marginNewHybrid * additionalCases
    personnelRelief = (1 - safeDivide(input.ambulationWithout, input.dischargeWithout)) * economics.effectiveRelief
    timeSavedMinutes = Math.max(0, input.dischargeWithout - input.ambulationWithout) * economics.relevantCases
    formula = 'DB = Wechselvorteil + Grenzkosten-DB aus Prozesszeitgewinn.'
    rationale = 'Die Prozessumstellung erzeugt Kapazität, ein klinischer Risiko- oder QALY-Effekt wird nicht unterstellt.'
  }

  const occupancy = occupancyForScenario(input, flags)
  const qaly = flags.device ? qalyFor(additionalCases) : 0
  const deviceCost = flags.device ? deviceCostFor(additionalCases) : 0

  return {
    id: definition.id,
    name: definition.name,
    shortName: definition.shortName,
    flags,
    tone: definition.tone,
    additionalCases,
    additionalCasesPct: safeDivide(additionalCases, economics.relevantCases),
    contribution,
    contributionPerCase: safeDivide(contribution, economics.relevantCases + additionalCases),
    personnelRelief,
    qaly,
    timeSavedMinutes,
    deviceCost,
    occupancy,
    formula,
    rationale,
  }
}

export function calculateAllScenarios(input: SimulationInputs): ScenarioResult[] {
  return scenarioDefinitions.map(definition => calculateScenario(input, definition))
}

export function formatNumber(value: number, maximumFractionDigits = 1) {
  return new Intl.NumberFormat('de-DE', { maximumFractionDigits, minimumFractionDigits: 0 }).format(value)
}

export function formatCurrency(value: number) {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value)
}

export function formatPercent(value: number, maximumFractionDigits = 1) {
  return new Intl.NumberFormat('de-DE', {
    style: 'percent',
    maximumFractionDigits,
  }).format(value)
}

export function formulaRows(input: SimulationInputs, economics: EconomicBreakdown) {
  return [
    {
      name: 'Relevante Fälle',
      value: economics.relevantCases,
      formula: 'Gesamtfälle × Einschlussquote',
      source: 'MasterEco!H20 × J20',
    },
    {
      name: 'Erlös Kurzlieger nach MD-/Arztzeitrisiko',
      value: economics.shortStayAfterAudit,
      formula: 'Bewertungsrelation × Basisrate × (1 − Prüfquote × reduzierter Anteil) + reduzierter Fallwert × Risiko + Pauschale − Arztzeit × Multiplikator × Prüfquote',
      source: 'MasterMD!H29',
    },
    {
      name: 'Wechselvorteil Hybrid-DRG',
      value: economics.hybridSwitchBenefit,
      formula: 'Hybrid-Erlös − Kurzliegererlös nach Risiko + Opportunitätswert Übernachtung',
      source: 'hiddencalc!C21',
    },
    {
      name: 'Grenzkosten-DB neuer Hybrid-Fälle',
      value: economics.marginNewHybrid,
      formula: 'Wechselvorteil + (Hybrid-Erlös − Wechselvorteil) × Grenzkostenanteil',
      source: 'hiddencalc!C25',
    },
    {
      name: 'Reduktion Blutungsereignisse',
      value: economics.bleedReduction,
      formula: '(Ereignisrate ohne − Ereignisrate mit) ÷ Ereignisrate ohne',
      source: 'hiddencalc!C35',
    },
  ]
}
